import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import {
  StockPrimaryButton,
  StockQuietButton,
  StockSurfaceCard,
} from 'src/components/stock/StockPageChrome'
import {
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import {
  WASTAGE_COPY,
  WASTAGE_NOTE_MAX_LENGTH,
  WASTAGE_REASON_CHIPS,
  wastageAboutCost,
  wastageAlreadyOnDay,
  wastageEntryDateLabel,
  wastageFrequentProducts,
  wastageKeptReason,
  wastageLastEntryByProduct,
  wastageLastEntryByRecipe,
  wastagePreviewCostPence,
  wastageRecentProductRange,
  wastageRecipePreviewPence,
  wastageRecipeUnit,
  wastageRecipeUnits,
  wastageSelectedUnit,
  wastageQuantityNeedsConfirm,
  wastageTypedQuantity,
  wastageUnitOptions,
} from 'src/constants/wastage'
import { API } from 'src/utils/interceptor'
import {
  createWastageEntry,
  deleteWastageEntry,
  editWastageEntry,
  listWastageEntries,
} from 'src/utils/wastageApi'

const poundsFrom = value => {
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount <= 0) return null
  return amount
}

export const normalizeWastageProduct = product => {
  const nested = product?.product || {}
  const id = product?._id || product?.id
  if (!id) return null
  return {
    id: String(id),
    title: product?.title || nested.title || 'Product',
    sku: nested.sku || product?.sku || '',
    supplier: String(product?.supplierTitle || product?.supplier || '').trim(),
    unit: product?.UoM || nested.UoM || '',
    conversions: Array.isArray(product?.conversions) ? product.conversions : [],
    pricePounds:
      poundsFrom(nested.price) ??
      poundsFrom(product?.price) ??
      poundsFrom(nested.OMSPrice) ??
      poundsFrom(product?.OMSPrice),
  }
}

export const normalizeWastageRecipe = recipe => {
  const id = recipe?._id || recipe?.id
  const title = String(recipe?.title || '').trim()
  if (!id || !title) return null
  const portions = Number(recipe?.portions)
  return {
    id: String(id),
    kind: 'recipe',
    title,
    portions: Number.isFinite(portions) && portions > 0 ? portions : 1,
    costPerPortion: poundsFrom(recipe?.costPerPortion),
    costPerRecipe: poundsFrom(recipe?.costPerRecipe),
  }
}

export const filterWastageProducts = (products, query) => {
  const needle = String(query || '')
    .trim()
    .toLowerCase()
  if (!needle) return []
  return (products || [])
    .filter(product =>
      [product.title, product.sku, product.supplier].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(needle),
      ),
    )
    .slice(0, 8)
}

export const filterWastageSearch = (products, recipes, query) => {
  const needle = String(query || '')
    .trim()
    .toLowerCase()
  if (!needle) return []
  const productHits = (products || []).filter(product =>
    [product.title, product.sku, product.supplier].some(value =>
      String(value || '')
        .toLowerCase()
        .includes(needle),
    ),
  )
  const recipeHits = (recipes || []).filter(recipe =>
    String(recipe.title || '')
      .toLowerCase()
      .includes(needle),
  )
  return mixWastageSearch(productHits, recipeHits)
}

const WASTAGE_SEARCH_LIMIT = 8
const WASTAGE_RECIPE_ROOM = 4

/** Products fill the list, and a matching dish keeps a place beside them. */
const mixWastageSearch = (productHits, recipeHits) => {
  const dishes = recipeHits.slice(0, WASTAGE_RECIPE_ROOM)
  const shownProducts = productHits.slice(
    0,
    WASTAGE_SEARCH_LIMIT - dishes.length,
  )
  const spare = WASTAGE_SEARCH_LIMIT - shownProducts.length - dishes.length
  const moreDishes = recipeHits.slice(
    WASTAGE_RECIPE_ROOM,
    WASTAGE_RECIPE_ROOM + spare,
  )
  return [...shownProducts, ...dishes, ...moreDishes]
}

export async function loadWastageProducts(venueId, { apiGet = API.get } = {}) {
  const data = await apiGet('api', `venue/${venueId}/products`)
  const rows = Array.isArray(data) ? data : []
  return rows.map(normalizeWastageProduct).filter(Boolean)
}

export async function loadWastageRecipes(venueId, { apiGet = API.get } = {}) {
  const data = await apiGet('api2', `recipes/venue/${venueId}`)
  const rows = Array.isArray(data?.recipes) ? data.recipes : []
  return rows.map(normalizeWastageRecipe).filter(Boolean)
}

const productLine = product =>
  [product.supplier, product.unit].filter(Boolean).join(' · ')

const selectionFromEntry = (entry, products, recipes) => {
  const recipe = entry?.kind === 'recipe'
  if (recipe) {
    const fromCatalogue = recipes.find(
      item => item.id === String(entry.recipeId),
    ) || {
      id: String(entry.recipeId || ''),
      title: entry.title,
      portions: 1,
    }
    return {
      ...fromCatalogue,
      kind: 'recipe',
      inputUnit: wastageRecipeUnit(
        { inputUnit: entry.inputUnit },
        fromCatalogue,
      ),
      conversionId: '',
      conversionFactor: 1,
    }
  }
  const fromCatalogue = products.find(
    item => item.id === String(entry.venueProductId),
  ) || {
    id: String(entry.venueProductId || ''),
    title: entry.title,
    supplier: entry.supplierTitle || '',
    unit: entry.baseUoM || entry.UoM || '',
    conversions: [],
  }
  return {
    ...fromCatalogue,
    inputUnit: entry.inputUnit || fromCatalogue.unit || '',
    conversionId: entry.conversionId ? String(entry.conversionId) : '',
    conversionFactor: entry.conversionFactor ?? (entry.conversionId ? null : 1),
  }
}

export const WasteRecordForm = ({
  venueId,
  today,
  listEntries = listWastageEntries,
  loadProducts = loadWastageProducts,
  loadRecipes = loadWastageRecipes,
  createEntry = createWastageEntry,
  updateEntry = editWastageEntry,
  removeEntry = deleteWastageEntry,
  entry = null,
  repeat = null,
  repeatToken = 0,
  presetProductId = '',
  reloadKey = 0,
  onSaved,
  onUndone,
  onCancel,
}) => {
  const searchRef = useRef(null)
  const formRef = useRef(null)
  const qtyRef = useRef(null)
  const chosenId = useRef('')
  const selectedRef = useRef(null)
  const prefilledId = useRef('')
  const prefilledRepeat = useRef(0)
  const presetApplied = useRef('')
  const editingId = entry?._id || entry?.id || ''
  const repeatId = repeat?._id || repeat?.id || ''
  const [products, setProducts] = useState([])
  const [recipes, setRecipes] = useState([])
  const [recent, setRecent] = useState([])
  const [logged, setLogged] = useState([])
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(null)
  selectedRef.current = selected
  const [quantity, setQuantity] = useState('')
  const [businessDate, setBusinessDate] = useState(today || '')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')
  const reasonRef = useRef('')
  reasonRef.current = reason
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [askLarge, setAskLarge] = useState(false)
  const [suggestionReload, setSuggestionReload] = useState(0)
  const [undoId, setUndoId] = useState('')
  const [undoing, setUndoing] = useState(false)
  const [catalogueReady, setCatalogueReady] = useState(false)
  const [loggedReady, setLoggedReady] = useState(false)

  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    const range = wastageRecentProductRange(today)
    listEntries(venueId, { ...range, includeRemoved: true })
      .then(data => {
        if (!cancelled) {
          const entries = Array.isArray(data?.entries) ? data.entries : []
          setLogged(entries)
          setRecent(wastageFrequentProducts(entries))
          setLoggedReady(true)
        }
        return null
      })
      .catch(() => {
        if (!cancelled) {
          setLogged([])
          setRecent([])
          setLoggedReady(true)
        }
        return null
      })
    loadProducts(venueId)
      .then(rows => {
        if (!cancelled) {
          setProducts(Array.isArray(rows) ? rows : [])
          setCatalogueReady(true)
        }
        return null
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([])
          setCatalogueReady(true)
        }
        return null
      })
    loadRecipes(venueId)
      .then(rows => {
        if (!cancelled) setRecipes(Array.isArray(rows) ? rows : [])
        return null
      })
      .catch(() => {
        if (!cancelled) setRecipes([])
        return null
      })
    return () => {
      cancelled = true
    }
  }, [
    venueId,
    today,
    listEntries,
    loadProducts,
    loadRecipes,
    suggestionReload,
    reloadKey,
  ])

  useEffect(() => {
    if (editingId || repeatId || presetProductId) return undefined
    searchRef.current?.focus()
    return undefined
  }, [editingId, repeatId, presetProductId])

  useEffect(() => {
    if (!editingId || !entry) {
      if (!editingId) prefilledId.current = ''
      return undefined
    }
    if (prefilledId.current === editingId) return undefined
    prefilledId.current = editingId
    setSelected(selectionFromEntry(entry, products, recipes))
    setQuantity(entry.inputQty == null ? '' : String(entry.inputQty))
    setReason(entry.reason || '')
    setBusinessDate(entry.businessDate || today || '')
    setNote(entry.note || '')
    setQuery('')
    setSaved(false)
    setUndoId('')
    setError('')
    qtyRef.current?.focus()
    formRef.current?.scrollIntoView?.({ block: 'nearest' })
    return undefined
  }, [editingId, entry, products, recipes, today])

  useEffect(() => {
    if (editingId || !repeat || prefilledRepeat.current === repeatToken)
      return undefined
    prefilledRepeat.current = repeatToken
    setSelected(selectionFromEntry(repeat, products, recipes))
    setQuantity('1')
    setReason(repeat.reason || '')
    setBusinessDate(today || '')
    setNote('')
    setQuery('')
    setSaved(false)
    setUndoId('')
    setError('')
    qtyRef.current?.focus()
    formRef.current?.scrollIntoView?.({ block: 'nearest' })
    return undefined
  }, [editingId, repeat, repeatToken, products, recipes, today])

  useEffect(() => {
    if (!editingId && !repeatId) return undefined
    setSelected(current => {
      if (!current) return current
      if (current.kind === 'recipe') {
        const match = recipes.find(item => item.id === current.id)
        if (!match || current.costPerPortion === match.costPerPortion)
          return current
        return {
          ...match,
          kind: 'recipe',
          inputUnit: current.inputUnit,
          conversionId: '',
          conversionFactor: 1,
        }
      }
      const match = products.find(item => item.id === current.id)
      if (!match) return current
      if (
        current.pricePounds === match.pricePounds &&
        current.conversions === match.conversions
      ) {
        return current
      }
      return {
        ...match,
        inputUnit: current.inputUnit || match.unit,
        conversionId: current.conversionId || '',
        conversionFactor: current.conversionFactor ?? 1,
      }
    })
    return undefined
  }, [products, recipes, editingId, repeatId])

  const recordDay = businessDate || today
  const alreadyOnDay = selected?.id
    ? wastageAlreadyOnDay(logged, {
        kind: selected.kind,
        id: selected.id,
        businessDate: recordDay,
        ignoreId: editingId,
      })
    : false
  const alreadyLine = alreadyOnDay
    ? recordDay === today
      ? WASTAGE_COPY.alreadyToday
      : WASTAGE_COPY.alreadyThatDay
    : ''
  const dateLabel =
    !businessDate || businessDate === today
      ? WASTAGE_COPY.dateToday
      : wastageEntryDateLabel(businessDate, today)
  const matches = useMemo(
    () => filterWastageSearch(products, recipes, query),
    [products, recipes, query],
  )
  const lastByProduct = useMemo(
    () => wastageLastEntryByProduct(logged),
    [logged],
  )
  const lastByRecipe = useMemo(() => wastageLastEntryByRecipe(logged), [logged])
  const previewBaseQty = () => {
    if (selected?.conversionId) {
      const factor = Number(selected.conversionFactor)
      if (!Number.isFinite(factor) || factor <= 0) return null
      const qty = wastageTypedQuantity(quantity)
      if (!Number.isFinite(qty) || qty <= 0) return null
      return qty / factor
    }
    return quantity
  }
  const preview = wastageAboutCost(
    selected?.kind === 'recipe'
      ? wastageRecipePreviewPence(selected, selected.inputUnit, quantity)
      : wastagePreviewCostPence(selected?.pricePounds, previewBaseQty()),
  )
  const unitOptions = !selected
    ? []
    : selected.kind === 'recipe'
    ? wastageRecipeUnits(selected)
    : wastageUnitOptions(selected, selected)

  const withUnit = (product, last) => {
    const choice = wastageSelectedUnit(product, last)
    return {
      ...product,
      inputUnit: choice.unit,
      conversionId: choice.conversionId,
      conversionFactor: choice.factor,
    }
  }

  const rememberChoice = (next, lastReason) => {
    const current = selectedRef.current
    const sameItem =
      current &&
      String(current.id) === String(next?.id) &&
      (current.kind || 'product') === (next?.kind || 'product')
    if (!sameItem) setQuantity('1')
    setSelected(next)
    setReason(wastageKeptReason(lastReason, reasonRef.current))
    setQuery('')
    setSaved(false)
    setError('')
    if (!editingId) qtyRef.current?.focus()
  }

  const applyChoice = (product, last) => {
    const fromCatalogue =
      products.find(item => item.id === product.id) || product
    rememberChoice(withUnit(fromCatalogue, last), last?.reason)
  }

  const applyRecipe = (recipe, last) => {
    const fromCatalogue = recipes.find(item => item.id === recipe.id) || recipe
    rememberChoice(
      {
        ...fromCatalogue,
        kind: 'recipe',
        inputUnit: wastageRecipeUnit(last, fromCatalogue),
        conversionId: '',
        conversionFactor: 1,
      },
      last?.reason,
    )
  }

  const choose = product => {
    const id = String(product.id)
    chosenId.current = id
    if (editingId) {
      const entryIsRecipe = entry?.kind === 'recipe'
      const pickIsRecipe = product.kind === 'recipe'
      if (entryIsRecipe !== pickIsRecipe) {
        setSaved(false)
        setError(
          entryIsRecipe ? 'Choose another recipe' : 'Choose another product',
        )
        return undefined
      }
    }
    if (product.kind === 'recipe') {
      const known = lastByRecipe.get(id)
      if (known) {
        applyRecipe(product, known)
        return undefined
      }
      applyRecipe(product, null)
      return listEntries(venueId, { recipe: id })
        .then(data => {
          if (chosenId.current !== id) return null
          const last = wastageLastEntryByRecipe(data?.entries).get(id)
          if (!last) return null
          setSelected(current => {
            if (!current || current.id !== id) return current
            return { ...current, inputUnit: wastageRecipeUnit(last, current) }
          })
          setReason(wastageKeptReason(last.reason, reasonRef.current))
          return null
        })
        .catch(() => null)
    }
    const known = lastByProduct.get(id)
    if (known) {
      applyChoice(product, known)
      return undefined
    }
    applyChoice(product, null)
    return listEntries(venueId, { product: id })
      .then(data => {
        if (chosenId.current !== id) return null
        const last = wastageLastEntryByProduct(data?.entries).get(id)
        if (!last) return null
        setSelected(current => {
          if (!current || current.id !== id) return current
          return withUnit(current, last)
        })
        setReason(wastageKeptReason(last.reason, reasonRef.current))
        return null
      })
      .catch(() => null)
  }

  useEffect(() => {
    if (!presetProductId || editingId || repeatId) return undefined
    if (!catalogueReady || !loggedReady) return undefined
    const id = String(presetProductId)
    if (presetApplied.current === id) return undefined
    const product = products.find(item => item.id === id)
    if (!product) return undefined
    presetApplied.current = id
    choose(product)
    return undefined
  }, [
    presetProductId,
    catalogueReady,
    loggedReady,
    products,
    editingId,
    repeatId,
    logged,
  ])

  const resetForNext = savedEntry => {
    const savedId = savedEntry?._id || savedEntry?.id || ''
    setSelected(null)
    setQuery('')
    setQuantity('')
    setBusinessDate(today || '')
    setNote('')
    setSaved(true)
    setUndoId(savedId ? String(savedId) : '')
    setError('')
    searchRef.current?.focus()
  }

  const onUndo = () => {
    if (!undoId || undoing) return undefined
    setUndoing(true)
    setError('')
    return removeEntry(venueId, undoId)
      .then(() => {
        setUndoId('')
        setSaved(false)
        setSuggestionReload(value => value + 1)
        if (onUndone) onUndone(undoId)
        return null
      })
      .catch(err => {
        setError(err?.message || 'Waste could not be removed')
        return null
      })
      .finally(() => {
        setUndoing(false)
      })
  }

  useEffect(() => {
    if (!askLarge) return undefined
    const onKey = event => {
      if (event.key === 'Escape') setAskLarge(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [askLarge])

  const persist = qty => {
    if (saving) return undefined
    const day = businessDate || today
    setAskLarge(false)
    setSaving(true)
    setError('')
    const body =
      selected.kind === 'recipe'
        ? {
            kind: 'recipe',
            recipeId: selected.id,
            inputQty: qty,
            inputUnit: selected.inputUnit || WASTAGE_COPY.portions,
            reason,
            businessDate: day,
          }
        : {
            venueProductId: selected.id,
            inputQty: qty,
            inputUnit: selected.inputUnit || selected.unit || undefined,
            reason,
            businessDate: day,
          }
    const trimmedNote = note.trim()
    if (editingId || trimmedNote) body.note = trimmedNote
    if (editingId && selected.kind !== 'recipe') {
      body.conversionId = selected.conversionId || ''
    } else if (selected.kind !== 'recipe' && selected.conversionId) {
      body.conversionId = selected.conversionId
    }
    const save = editingId
      ? updateEntry(venueId, editingId, body)
      : createEntry(venueId, body)
    return save
      .then(created => {
        setSuggestionReload(value => value + 1)
        resetForNext(editingId ? null : created)
        if (onSaved) onSaved(created)
        return null
      })
      .catch(err => {
        setSaved(false)
        setError(err?.message || 'Waste could not be saved')
        return null
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const onSubmit = event => {
    event.preventDefault()
    if (saving) return
    if (!selected?.id) {
      setError('Choose a product')
      setSaved(false)
      return
    }
    const qty = wastageTypedQuantity(quantity)
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Enter a quantity')
      setSaved(false)
      return
    }
    if (!reason) {
      setError('Choose a reason')
      setSaved(false)
      return
    }
    const day = businessDate || today
    if (today && day > today) {
      setError(WASTAGE_COPY.futureDay)
      setSaved(false)
      return
    }
    if (wastageQuantityNeedsConfirm(qty)) {
      setAskLarge(true)
      return
    }
    return persist(qty)
  }

  return (
    <>
      <Form
        ref={formRef}
        data-testid='waste-record-form'
        noValidate
        onSubmit={onSubmit}
      >
        {!editingId && recent.length ? (
          <Suggestions data-testid='waste-recent-products'>
            <SuggestLabel>{WASTAGE_COPY.loggedBefore}</SuggestLabel>
            <Recent>
              {recent.map(product => (
                <Chip
                  key={`${product.kind || 'product'}-${product.id}`}
                  type='button'
                  data-testid={`waste-recent-${product.id}`}
                  data-kind={product.kind || 'product'}
                  aria-pressed={selected?.id === product.id}
                  onClick={() => choose(product)}
                >
                  {product.kind === 'recipe'
                    ? `${product.title} · ${WASTAGE_COPY.recipe}`
                    : product.title}
                </Chip>
              ))}
            </Recent>
          </Suggestions>
        ) : null}
        <Search
          ref={searchRef}
          data-testid='waste-search'
          type='search'
          value={query}
          placeholder={WASTAGE_COPY.search}
          onChange={event => {
            setQuery(event.target.value)
            setSaved(false)
          }}
        />
        {catalogueReady && String(query).trim() && !matches.length ? (
          <SearchEmpty data-testid='waste-search-empty'>
            {WASTAGE_COPY.searchEmpty}
          </SearchEmpty>
        ) : null}
        {matches.length ? (
          <Results data-testid='waste-search-results'>
            {matches.map(product => (
              <Result
                key={product.id}
                type='button'
                data-testid={`waste-search-${product.id}`}
                onClick={() => choose(product)}
              >
                <ResultTitle>{product.title}</ResultTitle>
                {product.kind === 'recipe' ? (
                  <ResultMeta>{WASTAGE_COPY.recipe}</ResultMeta>
                ) : productLine(product) ? (
                  <ResultMeta>{productLine(product)}</ResultMeta>
                ) : null}
              </Result>
            ))}
          </Results>
        ) : null}
        {selected ? (
          <Selected data-testid='waste-selected-product'>
            <ResultTitle>{selected.title}</ResultTitle>
            {selected.kind === 'recipe' ? (
              <ResultMeta>{WASTAGE_COPY.recipe}</ResultMeta>
            ) : productLine(selected) ? (
              <ResultMeta>{productLine(selected)}</ResultMeta>
            ) : null}
          </Selected>
        ) : null}
        <QuantityRow>
          <Quantity
            ref={qtyRef}
            data-testid='waste-qty'
            inputMode='decimal'
            value={quantity}
            placeholder=''
            onChange={event => {
              setQuantity(event.target.value)
              setSaved(false)
              setError('')
            }}
          />
          {selected?.inputUnit ? (
            <Unit data-testid='waste-unit'>{selected.inputUnit}</Unit>
          ) : null}
        </QuantityRow>
        {unitOptions.length > 1 ? (
          <Recent data-testid='waste-units'>
            {unitOptions.map(option => (
              <Chip
                key={option.id}
                type='button'
                data-testid={`waste-unit-option-${option.id}`}
                aria-pressed={
                  String(option.conversionId || '') ===
                    String(selected?.conversionId || '') &&
                  option.unit === selected?.inputUnit
                }
                onClick={() => {
                  setSelected(current =>
                    current
                      ? {
                          ...current,
                          inputUnit: option.unit,
                          conversionId: option.conversionId,
                          conversionFactor: option.factor,
                        }
                      : current,
                  )
                  setSaved(false)
                }}
              >
                {option.label}
              </Chip>
            ))}
          </Recent>
        ) : null}
        {preview ? (
          <About data-testid='waste-about-cost'>{preview}</About>
        ) : null}
        <DateRow>
          <DateLine data-testid='waste-record-date'>{dateLabel}</DateLine>
          <DateInput
            data-testid='waste-date'
            type='date'
            aria-label='Day'
            value={businessDate}
            onChange={event => {
              const next = event.target.value
              setBusinessDate(next)
              setSaved(false)
              if (today && next > today) {
                setError(WASTAGE_COPY.futureDay)
                return
              }
              setError('')
            }}
          />
        </DateRow>
        <Reasons>
          {WASTAGE_REASON_CHIPS.map(chip => (
            <Chip
              key={chip.id}
              type='button'
              data-testid={`waste-reason-${chip.id}`}
              aria-pressed={reason === chip.id}
              onClick={() => {
                setReason(chip.id)
                setSaved(false)
                setError('')
              }}
            >
              {chip.label}
            </Chip>
          ))}
        </Reasons>
        <Note
          data-testid='waste-note'
          aria-label={WASTAGE_COPY.note}
          placeholder={WASTAGE_COPY.note}
          maxLength={WASTAGE_NOTE_MAX_LENGTH}
          value={note}
          onChange={event => {
            setNote(event.target.value)
            setSaved(false)
          }}
        />
        {alreadyLine ? (
          <Already data-testid='waste-already-today'>{alreadyLine}</Already>
        ) : null}
        <Actions>
          <StockPrimaryButton
            type='submit'
            data-testid='waste-save'
            disabled={saving}
          >
            {editingId ? WASTAGE_COPY.save : WASTAGE_COPY.record}
          </StockPrimaryButton>
          {editingId ? (
            <StockQuietButton
              type='button'
              data-testid='waste-edit-cancel'
              onClick={() => {
                if (onCancel) onCancel()
              }}
            >
              {WASTAGE_COPY.cancel}
            </StockQuietButton>
          ) : null}
          {saved ? (
            <Saved data-testid='waste-saved'>{WASTAGE_COPY.saved}</Saved>
          ) : null}
          {saved && undoId ? (
            <StockQuietButton
              type='button'
              data-testid='waste-undo'
              disabled={undoing}
              onClick={onUndo}
            >
              {WASTAGE_COPY.undo}
            </StockQuietButton>
          ) : null}
        </Actions>
        {error ? <Error data-testid='waste-record-error'>{error}</Error> : null}
      </Form>
      {askLarge ? (
        <AskScrim
          data-testid='waste-large-ask'
          onClick={() => setAskLarge(false)}
        >
          <AskCard
            role='dialog'
            aria-modal='true'
            aria-labelledby='waste-large-title'
            onClick={event => event.stopPropagation()}
          >
            <AskTitle id='waste-large-title'>
              {editingId ? WASTAGE_COPY.save : WASTAGE_COPY.record}
            </AskTitle>
            <AskQuestion>{WASTAGE_COPY.largeQuantity}</AskQuestion>
            <AskActions>
              <StockQuietButton
                type='button'
                data-testid='waste-large-cancel'
                onClick={() => setAskLarge(false)}
              >
                {WASTAGE_COPY.cancel}
              </StockQuietButton>
              <StockPrimaryButton
                type='button'
                data-testid='waste-large-confirm'
                onClick={() => persist(wastageTypedQuantity(quantity))}
              >
                {editingId ? WASTAGE_COPY.save : WASTAGE_COPY.record}
              </StockPrimaryButton>
            </AskActions>
          </AskCard>
        </AskScrim>
      ) : null}
    </>
  )
}

const Form = styled(StockSurfaceCard).attrs({ as: 'form' })`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`

const Suggestions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
`

const SuggestLabel = styled.div`
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.85rem;
  font-weight: 600;
`

const Recent = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const Chip = styled.button`
  margin: 0;
  padding: 0.4rem 0.75rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.2;
  cursor: pointer;

  &[aria-pressed='true'] {
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  }
`

const Search = styled.input`
  width: min(100%, 28rem);
  margin: 0;
  padding: 0.55rem 0.7rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  font-size: 1rem;
`

const SearchEmpty = styled.p`
  margin: 0.45rem 0 0;
  width: min(100%, 28rem);
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;
`

const Results = styled.div`
  display: flex;
  flex-direction: column;
  width: min(100%, 28rem);
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  overflow: hidden;
`

const Result = styled.button`
  margin: 0;
  padding: 0.55rem 0.7rem;
  border: 0;
  border-bottom: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }
`

const ResultTitle = styled.div`
  font-weight: 600;
`

const ResultMeta = styled.div`
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;
`

const Selected = styled.div``

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const Quantity = styled.input`
  width: 6rem;
  margin: 0;
  padding: 0.55rem 0.7rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  font-size: 1rem;
`

const Unit = styled.span`
  font-weight: 600;
`

const About = styled.div`
  margin-top: -0.35rem;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
`

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const DateLine = styled.div`
  font-weight: 600;
`

const DateInput = styled.input`
  margin: 0;
  padding: 0.45rem 0.6rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  font-size: 1rem;
`

const Note = styled.input`
  width: min(100%, 28rem);
  margin: 0;
  padding: 0.55rem 0.7rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  font-size: 1rem;
`

const Reasons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const Saved = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
`

const Already = styled.p`
  margin: 0;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.95rem;
`

const Error = styled.div`
  color: ${({ theme }) => theme.colours.chilliRed || '#b42318'};
  font-weight: 600;
`

const AskScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(19, 45, 7, 0.35);
`

const AskCard = styled.div`
  width: min(100%, 26rem);
  padding: 1.25rem;
  border-radius: 12px;
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
`

const AskTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  line-height: 1.3;
`

const AskQuestion = styled.p`
  margin: 0.6rem 0 0;
  font-size: 0.95rem;
  line-height: 1.45;
`

const AskActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.1rem;
`

export default WasteRecordForm
